import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock, AlertCircle, CheckCircle } from "lucide-react";
import { sendSignupConfirmationEmail, sendPasswordResetEmail } from "@/utils/emailService";
import { SocialLoginButtons } from "./SocialLoginButtons";
import { PasswordStrengthMeter, isPasswordStrong } from "./PasswordStrengthMeter";
import { isFeatureEnabled } from "@/config/featureFlags";
import { SignupSuccessModal } from "./SignupSuccessModal";

interface AuthSimplifiedProps {
  onAuthSuccess: () => void;
  onBrowseAnonymously?: () => void;
}

export const AuthSimplified = ({ onAuthSuccess, onBrowseAnonymously }: AuthSimplifiedProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { toast } = useToast();

  const getRedirectUrl = () => {
    // Always use the current origin to ensure emails redirect to the correct domain
    return `${window.location.origin}/profile/edit`;
  };

  const handleSignUp = async () => {
    console.log("Sign up attempt with email:", email);
    
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (!isPasswordStrong(password)) {
      toast({
        title: "Password too weak",
        description: "Please create a stronger password following the requirements below",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const redirectUrl = getRedirectUrl();
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        console.error("Sign up error:", error);
        setDebugInfo(`Sign up error: ${error.message}`);
        
        let errorMessage = error.message;
        if (error.message.includes("already registered")) {
          errorMessage = "This email is already registered. Please try signing in instead, or use the 'Forgot Password' option if you don't remember your password.";
        }
        
        toast({
          title: "Sign Up Failed",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        console.log("Sign up successful, data:", data);
        
        // Show success modal first
        setShowSuccessModal(true);
        
        // Store data for later use
        const needsConfirmation = data.user && !data.user.email_confirmed_at;
        
        // Send custom welcome email and show toast after modal is closed
        setTimeout(() => {
          if (needsConfirmation) {
            sendSignupConfirmationEmail(email, redirectUrl)
              .then(() => {
                setDebugInfo(`Sign up successful! Custom welcome email sent.`);
                toast({
                  title: "Account Created",
                  description: "We've sent you a personalized welcome email with your confirmation link. Please check your inbox and click the link to activate your account.",
                });
              })
              .catch((emailError) => {
                console.error("Failed to send custom email:", emailError);
                setDebugInfo(`Sign up successful but custom email failed. Fallback confirmation email sent.`);
                toast({
                  title: "Account Created",
                  description: "Please check your email for a confirmation link to activate your account.",
                });
              });
          } else {
            toast({
              title: "Success",
              description: "Account created! You can now sign in.",
            });
          }
        }, 500);
      }
    } catch (error) {
      console.error("Sign up catch error:", error);
      setDebugInfo(`Unexpected error: ${error}`);
      toast({
        title: "Error",
        description: "An unexpected error occurred during sign up. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    console.log("Sign in attempt with email:", email);
    
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Sign in error:", error);
        
        let errorMessage = "Unable to sign in. Please check your credentials.";
        let debugMessage = `Sign in error: ${error.message}`;
        
        if (error.message === "Invalid login credentials") {
          errorMessage = "Login failed. This could be because:\n\n• The email or password is incorrect\n• You haven't signed up with this email yet\n• You signed up but haven't confirmed your email address\n\nPlease check your credentials, sign up if you don't have an account, or check your email for a confirmation link.";
          debugMessage += "\n\nPossible causes:\n- Wrong email/password\n- Account doesn't exist\n- Email not confirmed";
        } else if (error.message.includes("Email not confirmed")) {
          errorMessage = "Please check your email and click the confirmation link before signing in.";
        }
        
        setDebugInfo(debugMessage);
        
        toast({
          title: "Unable to sign in",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        console.log("Sign in successful, calling onAuthSuccess");
        setDebugInfo(`Sign in successful! Welcome back.`);
        toast({
          title: "Welcome back!",
          description: "Signed in successfully to YieldSocial.",
        });
        onAuthSuccess();
      }
    } catch (error) {
      console.error("Sign in catch error:", error);
      setDebugInfo(`Unexpected error: ${error}`);
      toast({
        title: "Error",
        description: "An unexpected error occurred during sign in. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const checkUserExists = async () => {
    if (!email) {
      toast({
        title: "Enter email first",
        description: "Please enter an email address to reset password",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      const resetUrl = getRedirectUrl();
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetUrl
      });
      
      if (error) {
        console.error("Password reset error:", error);
        setDebugInfo(`Password reset error: ${error.message}`);
        toast({
          title: "Password Reset",
          description: "If an account with this email exists, we've sent a password reset link.",
        });
      } else {
        // Send custom password reset email
        try {
          await sendPasswordResetEmail(email, resetUrl);
          setDebugInfo(`Custom password reset email sent successfully`);
          toast({
            title: "Password Reset Email Sent",
            description: "We've sent you a personalized password reset link. Please check your email and follow the instructions to reset your password.",
          });
        } catch (emailError) {
          console.error("Failed to send custom reset email:", emailError);
          setDebugInfo(`Password reset initiated but custom email failed`);
          toast({
            title: "Password Reset Initiated",
            description: "If an account with this email exists, you'll receive a password reset link shortly.",
          });
        }
      }
    } catch (err) {
      console.error("Error resetting password:", err);
      setDebugInfo(`Error: ${err}`);
      toast({
        title: "Error",
        description: "An error occurred while trying to reset your password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage: `url('/lovable-uploads/d329c7ba-2a7b-448a-a1a8-6b098c770bef.png')`
      }}
    >
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/30"></div>
      
      <Card className="w-full max-w-md relative z-10 bg-white/95 backdrop-blur-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-green-800">Sign in or Sign up</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleSignIn()}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleSignIn()}
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleSignIn} 
                disabled={loading} 
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>

              {isFeatureEnabled('SOCIAL_LOGIN') && (
                <SocialLoginButtons onSuccess={onAuthSuccess} />
              )}
              
              <div className="space-y-2">
                <Button
                  onClick={checkUserExists}
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  disabled={loading}
                >
                  Forgot password? Reset it here
                </Button>
                
                {debugInfo && (
                  <div className="bg-blue-50 p-3 rounded text-xs">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="whitespace-pre-wrap text-blue-700">
                        {debugInfo}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {onBrowseAnonymously && (
                <Button
                  onClick={onBrowseAnonymously}
                  variant="ghost"
                  className="w-full text-gray-600 hover:text-gray-800"
                >
                  Continue Browsing Without Account
                </Button>
              )}

              <div className="text-sm text-center text-gray-600 mt-4 space-y-2">
                <p className="font-medium">Having trouble signing in?</p>
                <div className="text-xs space-y-1 text-left">
                  <p>• Make sure you've confirmed your email if you just signed up</p>
                  <p>• Check that your email and password are correct</p>
                  <p>• Try signing up if you don't have an account yet</p>
                  <p>• Use the "Forgot password" button if you can't remember your password</p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="signup" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="signup-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="signup-password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleSignUp()}
                  />
                </div>
                <PasswordStrengthMeter password={password} />
              </div>
              
              <Button 
                onClick={handleSignUp} 
                disabled={loading} 
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {loading ? "Creating account..." : "Sign Up"}
              </Button>

              {isFeatureEnabled('SOCIAL_LOGIN') && (
                <SocialLoginButtons onSuccess={onAuthSuccess} />
              )}
              
              {debugInfo && (
                <div className="bg-green-50 p-3 rounded text-xs">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="whitespace-pre-wrap text-green-700">
                      {debugInfo}
                    </div>
                  </div>
                </div>
              )}
              
              {onBrowseAnonymously && (
                <Button
                  onClick={onBrowseAnonymously}
                  variant="ghost"
                  className="w-full text-gray-600 hover:text-gray-800"
                >
                  Continue Browsing Without Account
                </Button>
              )}

              <div className="text-sm text-center text-gray-600 mt-4">
                <p>After signing up, check your email for a personalized welcome message.</p>
                <p className="text-green-600 font-medium mt-1">You must confirm your email before you can sign in.</p>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <SignupSuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </div>
  );
};
